import Phaser from "phaser";
import type { GameScene } from "./GameScene.ts";
import { getAudioManager } from "../systems/AudioManager.ts";
import { SaveManager } from "../systems/SaveManager.ts";
import { ART_RES, OUTLINE } from "../art/bake.ts";
import { ensureCoinTexture, ensureFuelCanTexture } from "../art/pickupArt.ts";
import {
  FUEL_BAR_SIZE,
  GAUGE_END,
  GAUGE_START,
  ensureFuelBarFillTexture,
  ensureGaugeTexture,
  ensurePauseIconTexture,
  ensurePedalTexture,
} from "../art/hudArt.ts";

const LABEL_STYLE: Phaser.Types.GameObjects.Text.TextStyle = {
  fontFamily: '"Arial Black", Arial, sans-serif',
  fontSize: "26px",
  color: "#ffffff",
  stroke: "#1d1f24",
  strokeThickness: 6,
};

interface Gauge {
  x: number;
  y: number;
  radius: number;
  value: number;
}

/** Heads-up display running in parallel with `GameScene`: fuel bar, coin counter, distance,
 * pause button, RPM/speed dials and the metal touch pedals. Reads run state each frame via
 * `GameScene.getHudSnapshot()` and writes touch input back via `touchGas`/`touchBrake`. All
 * art is canvas-baked (see `src/art/hudArt.ts`) and shown at 1/ART_RES scale. */
export class HUDScene extends Phaser.Scene {
  private gameScene!: GameScene;

  private distanceText!: Phaser.GameObjects.Text;
  private coinText!: Phaser.GameObjects.Text;
  private fuelFill!: Phaser.GameObjects.Image;
  private needles!: Phaser.GameObjects.Graphics;
  private rpmGauge!: Gauge;
  private speedGauge!: Gauge;
  private lowFuelTime = 0;

  constructor() {
    super({ key: "HUDScene" });
  }

  create(): void {
    this.gameScene = this.scene.get("GameScene") as GameScene;
    const { width, height } = this.scale;
    const scale = 1 / ART_RES;
    this.lowFuelTime = 0;
    // Allow gas + brake (or a pedal + pause) to be held with separate fingers.
    this.input.addPointer(2);

    // Fuel: can icon + framed glossy bar.
    this.add.image(32, 30, ensureFuelCanTexture(this)).setScale(scale * 0.78);
    const barX = 58;
    const barY = 30;
    const { width: barW, height: barH } = FUEL_BAR_SIZE;
    const frame = this.add.graphics();
    frame.fillStyle(OUTLINE, 1);
    frame.fillRoundedRect(barX - 4, barY - barH / 2 - 4, barW + 8, barH + 8, 8);
    frame.fillStyle(0x3a3f47, 1);
    frame.fillRoundedRect(barX, barY - barH / 2, barW, barH, 5);
    this.fuelFill = this.add
      .image(barX, barY, ensureFuelBarFillTexture(this))
      .setOrigin(0, 0.5)
      .setScale(scale);

    // Coins.
    this.add.image(32, 74, ensureCoinTexture(this, 0)).setScale(scale * 0.85);
    this.coinText = this.add.text(58, 74, "0", LABEL_STYLE).setOrigin(0, 0.5);

    this.distanceText = this.add
      .text(width / 2, 14, "0 m", { ...LABEL_STYLE, fontSize: "30px" })
      .setOrigin(0.5, 0);

    const pause = this.add
      .image(width - 36, 34, ensurePauseIconTexture(this))
      .setScale(scale)
      .setInteractive({ useHandCursor: true });
    pause.on("pointerdown", () => {
      getAudioManager().playUiClick();
      this.togglePause();
    });

    const swap = new SaveManager().getSettings().swapPedals;
    const leftX = 84;
    const rightX = width - 84;
    this.createPedal(swap ? rightX : leftX, height, "BRAKE", "brake");
    this.createPedal(swap ? leftX : rightX, height, "GAS", "gas");

    // Dials sit between the pedals; shrink them on narrow screens.
    const radius = Phaser.Math.Clamp(Math.floor((width - 340 - 48) / 4), 40, 62);
    const gaugeY = height - radius - 16;
    const gap = radius + 12;
    this.rpmGauge = { x: width / 2 - gap, y: gaugeY, radius, value: 0 };
    this.speedGauge = { x: width / 2 + gap, y: gaugeY, radius, value: 0 };
    for (const [gauge, label] of [
      [this.rpmGauge, "RPM"],
      [this.speedGauge, "SPEED"],
    ] as const) {
      this.add.image(gauge.x, gauge.y, ensureGaugeTexture(this, label, radius)).setScale(scale);
    }
    this.needles = this.add.graphics();
  }

  private createPedal(x: number, bottom: number, label: string, kind: "gas" | "brake"): void {
    const pedal = this.add
      .image(x, bottom + 12, ensurePedalTexture(this, label))
      .setOrigin(0.5, 1)
      .setScale(1 / ART_RES)
      .setInteractive();
    const restY = pedal.y;

    const setState = (down: boolean) => {
      if (kind === "gas") this.gameScene.touchGas = down;
      else this.gameScene.touchBrake = down;
      pedal.y = down ? restY + 6 : restY;
      if (down) pedal.setTint(0xc8ccd4);
      else pedal.clearTint();
    };

    pedal.on("pointerdown", () => setState(true));
    pedal.on("pointerup", () => setState(false));
    pedal.on("pointerout", () => setState(false));
  }

  private togglePause(): void {
    if (this.scene.isActive("PauseMenuScene") || this.scene.isActive("SettingsScene")) {
      this.gameScene.resumeFromPause();
    } else {
      this.gameScene.openPauseMenu();
    }
  }

  private drawNeedle(gauge: Gauge, target: number, dt: number): void {
    // Critically-damped-ish smoothing so the needle sweeps instead of jittering.
    gauge.value += (Phaser.Math.Clamp(target, 0, 1) - gauge.value) * Math.min(1, dt * 8);
    const angle = GAUGE_START + (GAUGE_END - GAUGE_START) * gauge.value;
    const len = gauge.radius - 14;
    const tipX = gauge.x + Math.cos(angle) * len;
    const tipY = gauge.y + Math.sin(angle) * len;
    const g = this.needles;
    g.lineStyle(7, OUTLINE, 1);
    g.lineBetween(gauge.x, gauge.y, tipX, tipY);
    g.lineStyle(3.5, 0xe8392f, 1);
    g.lineBetween(gauge.x, gauge.y, tipX, tipY);
    g.fillStyle(OUTLINE, 1);
    g.fillCircle(gauge.x, gauge.y, 8);
    g.fillStyle(0xc9ccd1, 1);
    g.fillCircle(gauge.x, gauge.y, 5);
  }

  override update(_time: number, delta: number): void {
    const dt = delta / 1000;
    const snapshot = this.gameScene.getHudSnapshot();
    this.distanceText.setText(`${Math.floor(snapshot.distanceMeters)} m`);
    this.coinText.setText(`${snapshot.coins}`);

    const fuel = Phaser.Math.Clamp(snapshot.fuelRatio, 0, 1);
    this.fuelFill.setCrop(0, 0, FUEL_BAR_SIZE.width * ART_RES * fuel, FUEL_BAR_SIZE.height * ART_RES);
    if (fuel < 0.2) {
      this.lowFuelTime += dt;
      this.fuelFill.setTint(0xff5a4a);
      this.fuelFill.setAlpha(0.55 + 0.45 * Math.abs(Math.sin(this.lowFuelTime * 6)));
    } else {
      this.lowFuelTime = 0;
      this.fuelFill.clearTint();
      this.fuelFill.setAlpha(1);
    }

    this.needles.clear();
    this.drawNeedle(this.rpmGauge, snapshot.rpmRatio, dt);
    this.drawNeedle(this.speedGauge, snapshot.speedRatio, dt);
  }
}
